import React from "react";
import {
  mdiCheckboxBlankOutline,
  mdiCheckboxMarked,
  mdiMinusBox,
} from "@mdi/js";
import {
  cn,
  getFocusRingStyles,
  incPanelColorLevel,
  panelBackgroundStyles,
} from "../../../styles";
import { ThemeColor } from "../../../props";
import { Icon } from "../../../atoms/Icon";
import { useDataTableContext } from "../context";
import { HeaderCell } from "./HeaderCell";
import { frozenCellStyle, frozenCellClassName } from "./frozenStyle";
import { FROZEN_SELECT_ID, FROZEN_EXPAND_ID } from "../hooks/useFrozenColumns";

export interface HeaderProps {
  className?: string;
}

export function Header({ className }: HeaderProps) {
  const { table, color, dense, selection, expansion, renderRowExpansion, alwaysExpanded, frozen } = useDataTableContext();
  const headerColor = incPanelColorLevel(color, 1);

  const selectFreezeInfo = frozen.getFreezeInfo(FROZEN_SELECT_ID);
  const expandFreezeInfo = frozen.getFreezeInfo(FROZEN_EXPAND_ID);

  const allSelected = selection?.isAllSelected(table.rows as any[]);
  const someSelected = selection?.isSomeSelected(table.rows as any[]);

  const handleSelectAll = () => {
    if (!selection) return;
    if (allSelected) {
      selection.deselectAll();
    } else {
      selection.selectAll(table.rows as any[]);
    }
  };

  return (
    <thead
      className={cn(
        "sticky top-0 z-10 border-b border-back-100 dark:border-back-800",
        panelBackgroundStyles[headerColor],
        className,
      )}
    >
      <tr>
        {selection && (
          <th
            ref={frozen.getCellRef(FROZEN_SELECT_ID)}
            className={cn(
              "w-14 p-0",
              panelBackgroundStyles[headerColor],
              frozenCellClassName(selectFreezeInfo),
            )}
            style={frozenCellStyle(selectFreezeInfo)}
          >
            <button
              onClick={handleSelectAll}
              className={cn(
                "flex items-center justify-center w-full cursor-pointer text-front-200 hover:text-front-400",
                "outline-none focus-visible:ring-2 focus-visible:ring-inset rounded",
                getFocusRingStyles(ThemeColor.primary),
                dense ? "px-1.5 py-2" : "px-2 py-1.5",
              )}
            >
              <Icon
                path={
                  allSelected
                    ? mdiCheckboxMarked
                    : someSelected
                      ? mdiMinusBox
                      : mdiCheckboxBlankOutline
                }
                size={18}
                className={
                  allSelected || someSelected ? "text-primary-600" : ""
                }
              />
            </button>
          </th>
        )}
        {table.columns
          .filter((col) => !col.hidden)
          .map((column) => (
            <HeaderCell key={column.id} column={column} />
          ))}
        {expansion && renderRowExpansion && !alwaysExpanded && (
          <th
            ref={frozen.getCellRef(FROZEN_EXPAND_ID)}
            className={cn(
              "w-8",
              dense ? "px-1.5 py-2" : "px-3 py-1.5",
              panelBackgroundStyles[headerColor],
              frozenCellClassName(expandFreezeInfo),
            )}
            style={frozenCellStyle(expandFreezeInfo)}
          />
        )}
      </tr>
    </thead>
  );
}
